// Frontend logging utility
export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug',
  HTTP = 'http'
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  requestId?: string;
  method?: string;
  url?: string;
  statusCode?: number;
  duration?: number;
  userAgent?: string;
  error?: any;
  metadata?: Record<string, any>;
}

class Logger {
  private isDevelopment = import.meta.env?.DEV || false;
  private logLevel: LogLevel;

  constructor() {
    // Set log level based on environment
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO, LogLevel.DEBUG, LogLevel.HTTP];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex <= currentLevelIndex;
  }

  private formatLog(entry: LogEntry): string {
    const { level, message, timestamp, requestId, method, url, statusCode, duration } = entry;
    
    let logMessage = `[${timestamp}] ${level.toUpperCase()}: ${message}`;
    
    if (requestId) {
      logMessage = `[${requestId}] ${logMessage}`;
    }
    
    if (method && url) {
      logMessage += ` - ${method} ${url}`;
    }
    
    if (statusCode) {
      logMessage += ` (${statusCode})`;
    }
    
    if (duration !== undefined) {
      logMessage += ` (${duration}ms)`;
    }
    
    return logMessage;
  }

  private log(level: LogLevel, message: string, metadata?: Record<string, any>): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...metadata
    };

    const formattedMessage = this.formatLog(entry);

    // Console logging with appropriate level
    switch (level) {
      case LogLevel.ERROR:
        console.error(formattedMessage, metadata);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, metadata);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, metadata);
        break;
      case LogLevel.DEBUG:
        console.debug(formattedMessage, metadata);
        break;
      case LogLevel.HTTP:
        console.log(formattedMessage, metadata);
        break;
    }

    // In production, you might want to send logs to a remote service
    if (!this.isDevelopment) {
      this.sendToRemoteService(entry);
    }
  }

  private async sendToRemoteService(_entry: LogEntry): Promise<void> {
    try {
      // Example: Send to your logging service
      // await fetch('/api/v1/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry)
      // });
    } catch (error) {
      console.error('Failed to send log to remote service:', error);
    }
  }

  error(message: string, error?: any, metadata?: Record<string, any>): void {
    this.log(LogLevel.ERROR, message, { error, ...metadata });
  }

  warn(message: string, metadata?: Record<string, any>): void {
    this.log(LogLevel.WARN, message, metadata);
  }

  info(message: string, metadata?: Record<string, any>): void {
    this.log(LogLevel.INFO, message, metadata);
  }

  debug(message: string, metadata?: Record<string, any>): void {
    this.log(LogLevel.DEBUG, message, metadata);
  }

  http(message: string, metadata?: Record<string, any>): void {
    this.log(LogLevel.HTTP, message, metadata);
  }

  // Request logging methods
  logRequest(method: string, url: string, requestId?: string, metadata?: Record<string, any>): void {
    this.http(`Request: ${method} ${url}`, {
      requestId,
      method,
      url,
      userAgent: navigator.userAgent,
      ...metadata
    });
  }

  logResponse(method: string, url: string, statusCode: number, duration: number, requestId?: string, metadata?: Record<string, any>): void {
    this.http(`Response: ${method} ${url} - ${statusCode} (${duration}ms)`, {
      requestId,
      method,
      url,
      statusCode,
      duration,
      ...metadata
    });
  }

  logError(method: string, url: string, error: any, duration: number, requestId?: string, metadata?: Record<string, any>): void {
    this.error(`Request failed: ${method} ${url}`, error, {
      requestId,
      method,
      url,
      duration,
      ...metadata
    });
  }
}

// Create and export logger instance
export const logger = new Logger();

// Export convenience methods
export const logError = (message: string, error?: any, metadata?: Record<string, any>) => 
  logger.error(message, error, metadata);

export const logWarn = (message: string, metadata?: Record<string, any>) => 
  logger.warn(message, metadata);

export const logInfo = (message: string, metadata?: Record<string, any>) => 
  logger.info(message, metadata);

export const logDebug = (message: string, metadata?: Record<string, any>) => 
  logger.debug(message, metadata);

export const logHttp = (message: string, metadata?: Record<string, any>) => 
  logger.http(message, metadata);
